import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useGetTicketDetailsQuery,
  useVerifyTicketMutation,
} from '@/store/api/eventsApi';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { CheckCircle, XCircle, AlertTriangle, Loader2, QrCode } from 'lucide-react';
import { Scanner } from '@yudiel/react-qr-scanner';

const VerifyTicketPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const ticketId = searchParams.get('ticketId') || '';

  const {
    data: ticketData,
    isLoading: isFetching,
    error: fetchError,
    refetch,
  } = useGetTicketDetailsQuery(ticketId, { skip: !ticketId });

  const [verifyTicket, { isLoading: isVerifying }] = useVerifyTicketMutation();
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);

  const [isScanning, setIsScanning] = useState(!ticketId);

  // If a ticketId is present, we shouldn't be scanning unless requested
  useEffect(() => {
    if (ticketId) {
      setIsScanning(false);
      setVerificationSuccess(false);
      setVerificationError(null);
    } else {
      setIsScanning(true);
    }
  }, [ticketId]);

  const handleVerify = async () => {
    try {
      setVerificationError(null);
      await verifyTicket(ticketId).unwrap();
      setVerificationSuccess(true);
      refetch(); // Refetch to get the updated status
    } catch (err: any) {
      setVerificationError(err.data?.message || 'Failed to verify ticket.');
    }
  };

  const handleScan = (text: string) => {
    if (text) {
      try {
        // Try parsing as URL first (our QR codes generate full URLs)
        const url = new URL(text);
        const scannedTicketId = url.searchParams.get('ticketId');
        if (scannedTicketId) {
          setSearchParams({ ticketId: scannedTicketId });
          return;
        }
      } catch (e) {
        // Not a valid URL, check if it looks like a direct OCCSSG ticket ID
        if (text.startsWith('OCCSSG-')) {
          setSearchParams({ ticketId: text });
          return;
        }
      }
      
      // Fallback: If we couldn't parse it, maybe show an error
      setVerificationError("Invalid QR Code. Does not contain a ticket ID.");
    }
  };

  if (isScanning) {
    return (
      <div className="flex h-[calc(100vh-100px)] items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-slate-200">
          <CardHeader className="text-center border-b border-slate-100 bg-white rounded-t-xl">
            <CardTitle className="text-2xl font-bold text-slate-800 flex items-center justify-center gap-2">
              <QrCode className="w-6 h-6" /> Scan Ticket
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 bg-black overflow-hidden relative" style={{ height: '400px' }}>
            <Scanner
              onScan={(result) => {
                if (result && result.length > 0 && result[0]?.rawValue) {
                  handleScan(result[0].rawValue);
                }
              }}
              onError={(error) => console.error(error)}
            />
          </CardContent>
          <CardFooter className="bg-slate-50 border-t border-slate-100 p-4 rounded-b-xl flex flex-col gap-3">
            <p className="text-sm text-center text-slate-600">
              Point your camera at the attendee's QR code.
            </p>
            {verificationError && (
              <div className="w-full bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm text-center font-medium mt-2">
                {verificationError}
              </div>
            )}
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-100px)] items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-slate-200">
        <CardHeader className="text-center border-b border-slate-100 bg-white rounded-t-xl flex justify-between flex-row items-center">
          <CardTitle className="text-xl font-bold text-slate-800">Ticket Scanner</CardTitle>
          <Button variant="outline" size="sm" onClick={() => setSearchParams({})}>
            <QrCode className="w-4 h-4 mr-2" /> Scan Another
          </Button>
        </CardHeader>
        <CardContent className="pt-6 bg-white min-h-[300px]">
          {isFetching ? (
            <div className="flex flex-col items-center justify-center py-10">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
              <p className="text-slate-600 font-medium">Looking up ticket details...</p>
            </div>
          ) : fetchError ? (
            <div className="text-center text-red-500 py-6 flex flex-col items-center">
              <XCircle className="w-16 h-16 mb-4 text-red-500" />
              <h2 className="text-xl font-bold">Ticket Not Found</h2>
              <p className="mt-2 text-slate-600">This ticket is invalid or does not exist.</p>
            </div>
          ) : ticketData ? (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-800">{ticketData.event?.title}</h3>
                <p className="text-sm text-slate-500">Ticket ID: <span className="font-mono">{ticketId}</span></p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                <p className="text-sm text-slate-500 mb-1">Attendee Name</p>
                <p className="font-semibold text-lg text-slate-900">{ticketData.fullName}</p>
                <p className="text-sm text-slate-500 mt-3 mb-1">Email</p>
                <p className="font-medium text-slate-800">{ticketData.email}</p>
              </div>

              {ticketData.hasAttended && !verificationSuccess ? (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-orange-500 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-orange-800">Already Checked In!</h4>
                    <p className="text-sm text-orange-700 mt-1">
                      This ticket was already scanned and used for entry.
                    </p>
                  </div>
                </div>
              ) : verificationSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex flex-col items-center text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 mb-2" />
                  <h4 className="font-bold text-green-800 text-xl">Access Granted</h4>
                  <p className="text-sm text-green-700 mt-1">
                    Attendee has been successfully checked in.
                  </p>
                </div>
              ) : null}

              {verificationError && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm text-center font-medium">
                  {verificationError}
                </div>
              )}
            </div>
          ) : null}
        </CardContent>

        {ticketData && !ticketData.hasAttended && !verificationSuccess && (
          <CardFooter className="bg-slate-50 border-t border-slate-100 p-4 rounded-b-xl flex flex-col gap-3">
            <Button
              className="w-full text-lg py-6 bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg transition-transform active:scale-[0.98]"
              onClick={handleVerify}
              disabled={isVerifying}
            >
              {isVerifying ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'CONFIRM CHECK-IN'
              )}
            </Button>
            <p className="text-xs text-center text-slate-500">
              Verify attendee identity before confirming.
            </p>
          </CardFooter>
        )}
      </Card>
    </div>
  );
};

export default VerifyTicketPage;
